import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    fetchParty,
    fetchPartyPromises,
    fetchPartyArticles,
    fetchPartyStats,
    fetchPoliticians,
    API,
} from "@/lib/api";import PromiseCard from "@/components/PromiseCard";
import ArticleCard from "@/components/ArticleCard";
import { TrustBar, DaysCounter } from "@/components/PartyCard";
import TrustHistoryChart from "@/components/TrustHistoryChart";
import FlipFlopCard from "@/components/FlipFlopCard";
import ShareButton from "@/components/ShareButton";
import {
    ChevronLeft,
    Users,
    Calendar,
    Flag,
    Globe,
    UserCheck,
    Target,
    Scale,
    Download,
    FileDown,
} from "lucide-react";
import { downloadCsv, today as todayStr } from "@/lib/csv";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";

export default function PartyDetail() {
    const { id } = useParams();
    const [party, setParty] = useState(null);
    const [promises, setPromises] = useState([]);
    const [articles, setArticles] = useState([]);
    const [stats, setStats] = useState(null);
    const [politicians, setPoliticians] = useState([]);
    const [tab, setTab] = useState("lofter");

    useEffect(() => {
        setParty(null);
        Promise.all([
            fetchParty(id),
            fetchPartyPromises(id),
            fetchPartyArticles(id),
            fetchPartyStats(id),
            fetchPoliticians({ party_id: id, sort: "mentions" }),
        ]).then(([p, pr, a, s, pol]) => {
            setParty(p);
            setPromises(pr);
            setArticles(a);
            setStats(s);
            setPoliticians(pol);
        });
    }, [id]);

    if (!party)
        return (
            <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#4F5A7A]">
                Indlæser partidata...
            </div>
        );

    const pieData = stats
        ? [
              { name: "Holdt", value: stats.promises.holdt, color: "#186A3B" },
              { name: "Delvist", value: stats.promises.delvist, color: "#C78300" },
              { name: "Brudt", value: stats.promises.brudt, color: "#BB1B2D" },
              { name: "Afventer", value: stats.promises.afventer, color: "#4F5A7A" },
          ].filter((d) => d.value > 0)
        : [];

    const ogTitle = stats
        ? `${party.name} — tillidsscore ${stats.trust_score}% | Løftebarometeret`
        : party.name;
    const ogDesc = party.long_description || party.description;
    const ogImage = `${API}/parties/${id}/og-image.png`;

    return (
        <div data-testid="party-detail-page">
            <Helmet>
                <title>{ogTitle}</title>
                <meta name="description" content={ogDesc.slice(0, 240)} />
                <meta name="keywords" content={`${party.name}, ${party.short_name}, ${party.leader}, ${party.ideology}, valgløfter, tillidsscore, Folketinget, dansk politik, politiske holdninger`} />
                <link rel="canonical" href={`${window.location.origin}/partier/${id}`} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDesc.slice(0, 240)} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={`${window.location.origin}/partier/${id}`} />
                <meta property="og:site_name" content="Løftebarometeret" />
                <meta property="og:locale" content="da_DK" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={ogTitle} />
                <meta name="twitter:description" content={ogDesc.slice(0, 240)} />
                <meta name="twitter:image" content={ogImage} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "PoliticalParty",
                        "name": party.name,
                        "alternateName": party.short_name,
                        "foundingDate": String(party.founded),
                        "description": party.long_description || party.description,
                        "url": `${window.location.origin}/partier/${id}`,
                        "member": (party.politicians || []).slice(0, 10).map((n) => ({
                            "@type": "Person",
                            "name": n,
                            "memberOf": { "@type": "PoliticalParty", "name": party.name },
                        })),
                        ...(party.leader && {
                            "leader": {
                                "@type": "Person",
                                "name": party.leader,
                            },
                        }),
                    })}
                </script>
            </Helmet>

            {/* HERO */}
            <section
                className="text-white relative overflow-hidden grain"
                style={{
                    background: `linear-gradient(135deg, ${party.color} 0%, #061845 100%)`,
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
                    <div className="flex items-center justify-between mb-6">
                        <Link
                            to="/partier"
                            className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm"
                            data-testid="back-to-parties"
                        >
                            <ChevronLeft className="w-4 h-4" /> Tilbage til partier
                        </Link>
                        <button
                            onClick={() => {
                                const slug = party.short_name.toLowerCase();
                                // Eksportér både løfter og artikler i ét ark (med type-kolonne)
                                const rows = [
                                    ["Type", "Titel", "Kategori", "Status / Artikeltype", "Dato", "Kilde / Note", "URL"],
                                ];
                                promises.forEach((p) => {
                                    rows.push([
                                        "Løfte",
                                        p.title,
                                        p.category,
                                        p.status,
                                        p.date_made,
                                        p.source,
                                        "",
                                    ]);
                                });
                                articles.forEach((a) => {
                                    rows.push([
                                        "Artikel",
                                        a.title,
                                        a.category || "",
                                        a.type,
                                        a.date,
                                        a.source,
                                        a.url,
                                    ]);
                                });
                                downloadCsv(rows, `loftebarometer-${slug}-${todayStr()}.csv`);
                            }}
                            data-testid="party-csv-download"
                            title="Download alle løfter og artikler som CSV"
                            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full text-sm font-semibold transition-colors"
                        >
                            <Download className="w-4 h-4" /> CSV
                        </button>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-4 mb-5">
                                <div
                                    className="w-20 h-20 rounded-xl bg-white flex items-center justify-center font-serif-dm text-5xl font-bold"
                                    style={{ color: party.color }}
                                >
                                    {party.short_name}
                                </div>
                                <div>
                                    <h1 className="font-serif-dm text-5xl leading-tight">{party.name}</h1>
                                    <p className="text-white/70 mt-1">{party.ideology}</p>
                                </div>
                            </div>
                            <p className="text-white/85 text-lg leading-relaxed max-w-3xl">
                                {party.long_description || party.description}
                            </p>
                            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-6 text-sm text-white/80">
                                <span className="inline-flex items-center gap-2">
                                    <Users className="w-4 h-4" /> Leder: <strong className="text-white">{party.leader}</strong>
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <Calendar className="w-4 h-4" /> Stiftet: <strong className="text-white">{party.founded}</strong>
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <Flag className="w-4 h-4" /> <strong className="text-white">{party.seats}</strong> mandater
                                </span>
                                {party.member_count && (
                                    <span className="inline-flex items-center gap-2">
                                        <UserCheck className="w-4 h-4" /> Ca. <strong className="text-white">{party.member_count.toLocaleString("da-DK")}</strong> medlemmer
                                    </span>
                                )}
                                {party.website && (
                                    <a
                                        href={party.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 hover:text-white underline underline-offset-2"
                                        data-testid="party-website"
                                    >
                                        <Globe className="w-4 h-4" /> {party.website.replace("https://", "")}
                                    </a>
                                )}
                                <a
                                    href={`${API}/parties/${party.id}/profile.pdf`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-sm transition-colors"
                                    data-testid="download-pdf-btn"
                                    title={`Hent partiprofil for ${party.name} som PDF`}
                                >
                                    <FileDown className="w-4 h-4" /> Hent som PDF
                                </a>
                                <ShareButton
                                    url={`${window.location.origin}/partier/${party.id}`}
                                    title={
                                        stats
                                            ? `${party.name} har ${Math.round(stats.trust_score)}/100 i tillidsscore på Løftebarometeret`
                                            : `${party.name} på Løftebarometeret`
                                    }
                                    hashtags={["dkpol", "Løftebarometeret"]}
                                    testIdPrefix="party-share"
                                    label="Del"
                                />
                            </div>
                        </div>

                        {stats && (
                            <div className="lg:col-span-4">
                                <div className="bg-white rounded-2xl p-6 text-[#0B1A3A]">
                                    <TrustBar score={stats.trust_score} />
                                    <div className="mt-4">
                                        <DaysCounter days={stats.days_since_last_break} size="lg" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-[#EEF0F7]">
                                        <StatBox label="Skandaler" value={stats.scandals_count || 0} color="#6B0F8C" />
                                        <StatBox label="Løgne" value={stats.lies_count} color="#BB1B2D" />
                                        <StatBox label="Valgflæsk" value={stats.valgflaesk_count} color="#C78300" />
                                        <StatBox label="Brudte løfter" value={stats.promises.brudt} color="#BB1B2D" />
                                    </div>
                                    {(stats.article_penalty || 0) > 0 && (
                                        <div
                                            className="mt-3 p-3 rounded-lg bg-[#F5E8FA] border border-[#D9B6E8] text-xs text-[#4A0A66] leading-relaxed"
                                            data-testid="trust-penalty-hint"
                                            title="Skandaler vægter 6 point pr. sag, løgne 1 point, valgflæsk 0,5 point — cappet ved 50 point."
                                        >
                                            <strong>−{stats.article_penalty} point</strong> trukket fra tillidsscoren
                                            (basis: {stats.base_score}/100) pga. dokumenterede skandaler, løgne og valgflæsk.
                                        </div>
                                    )}
                                    {stats.avg_difficulty > 0 && (
                                        <div className="mt-4 pt-4 border-t border-[#EEF0F7] text-xs text-[#4F5A7A]">
                                            Gennemsnitlig løfte-sværhedsgrad: <strong className="text-royal">{stats.avg_difficulty} / 5</strong>
                                        </div>
                                    )}
                                    {stats.longest_pending_days > 0 && (
                                        <div
                                            className="mt-2 text-xs text-[#4F5A7A]"
                                            data-testid="longest-pending-days"
                                        >
                                            Længst afventende løfte: <strong className="text-[#C78300]">{stats.longest_pending_days.toLocaleString("da-DK")} dage</strong>
                                        </div>
                                    )}
                                    {stats.flip_flop_total_topics > 0 && (
                                        <div
                                            className="mt-2 text-xs text-[#4F5A7A]"
                                            data-testid="flipflop-summary"
                                        >
                                            Flip flop-score: <strong style={{ color: stats.flip_flop_score >= 67 ? "#BB1B2D" : stats.flip_flop_score >= 34 ? "#C78300" : "#186A3B" }}>{stats.flip_flop_score}%</strong>
                                            {" "}({stats.flip_flop_shifted}/{stats.flip_flop_total_topics} emner skiftet)
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* KEY POLICIES + VOTER BASE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid md:grid-cols-3 gap-5">
                    {party.key_policies && party.key_policies.length > 0 && (
                        <div className="md:col-span-2 bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <Target className="w-5 h-5 text-royal" />
                                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                                    Mærkesager
                                </span>
                            </div>
                            <h2 className="font-serif-dm text-2xl md:text-3xl text-royal mb-5">
                                Hvad kæmper {party.name} for?
                            </h2>
                            <ul className="space-y-3">
                                {party.key_policies.map((policy, idx) => (
                                    <li
                                        key={`${party.id}-policy-${policy.slice(0, 40)}`}
                                        className="flex items-start gap-3 text-[#0B1A3A]"
                                        data-testid={`key-policy-${idx}`}
                                    >
                                        <span
                                            className="mt-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                                            style={{ backgroundColor: party.color }}
                                        >
                                            {idx + 1}
                                        </span>
                                        <span className="leading-relaxed">{policy}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {party.voter_base && (
                        <div className="bg-royal-soft border border-[#DCE3F3] rounded-2xl p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-3">
                                <Users className="w-5 h-5 text-royal" />
                                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                                    Vælgerbase
                                </span>
                            </div>
                            <p className="text-[#0B1A3A] leading-relaxed">{party.voter_base}</p>
                        </div>
                    )}
                </div>
            </section>

            {/* STANCES */}
            {party.stances && Object.keys(party.stances).length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Scale className="w-5 h-5 text-royal" />
                            <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                                Holdninger
                            </span>
                        </div>
                        <h2 className="font-serif-dm text-3xl md:text-4xl text-royal">
                            Hvor står partiet på de store emner?
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(party.stances).map(([topic, stance]) => (
                            <div
                                key={topic}
                                className="bg-white border border-[#E5E8F0] rounded-xl p-5 hover-lift"
                                data-testid={`stance-${topic}`}
                            >
                                <div
                                    className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white mb-3"
                                    style={{ backgroundColor: party.color }}
                                >
                                    {topic}
                                </div>
                                <p className="text-sm text-[#0B1A3A] leading-relaxed">{stance}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* HISTORISK TILLIDSGRAF */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <TrustHistoryChart partyId={id} defaultDays={30} />
            </section>

            {/* FLIP FLOP */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <FlipFlopCard partyId={id} partyColor={party.color} />
            </section>

            {/* PIE + TABS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {pieData.length > 0 && (
                    <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 md:p-8 mb-10">
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Løftebalance
                        </span>
                        <h2 className="font-serif-dm text-3xl text-royal mt-2 mb-6">
                            Fordeling af løfter
                        </h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={110}
                                    paddingAngle={3}
                                >
                                    {pieData.map((d) => (
                                        <Cell key={d.name} fill={d.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* TABS */}
                <div className="flex gap-2 border-b border-[#E5E8F0] mb-8">
                    <TabButton
                        active={tab === "lofter"}
                        onClick={() => setTab("lofter")}
                        testId="tab-promises"
                    >
                        Løfter ({promises.length})
                    </TabButton>
                    <TabButton
                        active={tab === "artikler"}
                        onClick={() => setTab("artikler")}
                        testId="tab-articles"
                    >
                        Artikler ({articles.length})
                    </TabButton>
                    {party.politicians && party.politicians.length > 0 && (
                        <TabButton
                            active={tab === "politikere"}
                            onClick={() => setTab("politikere")}
                            testId="tab-politicians"
                        >
                            Folketingsmedlemmer ({politicians.length || party.politicians.length})
                        </TabButton>
                    )}
                </div>

                {tab === "lofter" && (
                    <PromisesByPeriod promises={promises} />
                )}

                {tab === "artikler" && (
                    <div className="grid md:grid-cols-2 gap-4">
                        {articles.map((a) => (
                            <ArticleCard key={a.id} article={a} partyName={party.name} />
                        ))}
                        {articles.length === 0 && (
                            <p className="text-[#4F5A7A] col-span-full">Ingen artikler registreret endnu.</p>
                        )}
                    </div>
                )}

                {tab === "politikere" && (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {politicians.map((pol) => {
                            const initials = pol.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
                            const critical = pol.mentions_count > 0 && pol.truth_score < 50;
                            const good = pol.mentions_count === 0 || pol.truth_score >= 80;
                            const color = critical ? "#BB1B2D" : good ? "#186A3B" : "#C78300";
                            return (
                                <Link
                                    key={pol.id}
                                    to={`/politikere/${pol.id}`}
                                    className="bg-white border border-[#E5E8F0] rounded-xl p-5 hover-lift block"
                                    data-testid={`politician-${pol.name}`}
                                >
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center font-serif-dm text-lg font-bold text-white mb-3"
                                        style={{ backgroundColor: party.color }}
                                    >
                                        {initials}
                                    </div>
                                    <div className="font-semibold text-royal">{pol.name}</div>
                                    <div className="text-xs text-[#4F5A7A] mt-0.5">{pol.role}</div>
                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EEF0F7]">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-[#8A94AE]">Sandhed</div>
                                            <div className="font-serif-dm text-xl" style={{ color }}>
                                                {pol.truth_score}%
                                            </div>
                                        </div>
                                        <div className="text-[11px] text-right text-[#4F5A7A]">
                                            {pol.mentions_count} omtaler
                                            {pol.lies_count > 0 && (
                                                <div className="text-[#BB1B2D]">{pol.lies_count} løgne</div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                        {politicians.length === 0 && (
                            <p className="text-[#4F5A7A] col-span-full">Ingen politikere registreret endnu.</p>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}

function StatBox({ label, value, color }) {
    return (
        <div>
            <div className="font-serif-dm text-3xl" style={{ color }}>
                {value}
            </div>
            <div className="text-xs text-[#4F5A7A] mt-0.5">{label}</div>
        </div>
    );
}

function TabButton({ active, onClick, children, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                active
                    ? "border-royal text-royal"
                    : "border-transparent text-[#4F5A7A] hover:text-royal"
            }`}
        >
            {children}
        </button>
    );
}


function PromisesByPeriod({ promises }) {
    // Gruppér på election_year (kommer fra backend _enrich_promise)
    const groups = {};
    for (const p of promises) {
        const key = p.election_year ?? "ukendt";
        if (!groups[key]) {
            groups[key] = {
                year: p.election_year,
                period: p.election_period || "Ukendt periode",
                items: [],
                stats: { total: 0, holdt: 0, delvist: 0, brudt: 0, afventer: 0 },
            };
        }
        groups[key].items.push(p);
        groups[key].stats.total += 1;
        groups[key].stats[p.status] = (groups[key].stats[p.status] || 0) + 1;
    }
    const sortedKeys = Object.keys(groups).sort((a, b) => {
        if (a === "ukendt") return 1;
        if (b === "ukendt") return -1;
        return Number(b) - Number(a);
    });

    if (promises.length === 0) {
        return <p className="text-[#4F5A7A]">Ingen løfter registreret endnu.</p>;
    }

    return (
        <div className="space-y-8" data-testid="promises-by-period">
            {sortedKeys.map((k) => {
                const g = groups[k];
                const completed = g.stats.holdt + g.stats.delvist + g.stats.brudt;
                const keepRate =
                    completed > 0
                        ? Math.round((100 * (g.stats.holdt + 0.5 * g.stats.delvist)) / completed)
                        : null;
                return (
                    <section key={k} data-testid={`period-${k}`}>
                        <header className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                            <div className="flex items-baseline gap-3">
                                <h3 className="font-serif-dm text-xl text-royal">
                                    {g.period}
                                </h3>
                                <span className="text-xs uppercase tracking-wide font-semibold text-[#8A94AE]">
                                    {g.stats.total} {g.stats.total === 1 ? "løfte" : "løfter"}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                {keepRate !== null && (
                                    <span
                                        className={`px-2 py-1 rounded-full font-bold ${
                                            keepRate >= 65
                                                ? "bg-[#DCF5E4] text-[#186A3B]"
                                                : keepRate >= 40
                                                ? "bg-[#FFF2D1] text-[#7A5A00]"
                                                : "bg-[#FADDDF] text-[#8B1A1A]"
                                        }`}
                                        title="Holdte + halvdelen af delvist, ud af afsluttede løfter"
                                    >
                                        {keepRate}% holdt
                                    </span>
                                )}
                                {g.stats.holdt > 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-[#DCF5E4] text-[#186A3B]">
                                        {g.stats.holdt} holdt
                                    </span>
                                )}
                                {g.stats.brudt > 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-[#FADDDF] text-[#8B1A1A]">
                                        {g.stats.brudt} brudt
                                    </span>
                                )}
                                {g.stats.afventer > 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-[#EEF1F8] text-[#4F5A7A]">
                                        {g.stats.afventer} afventer
                                    </span>
                                )}
                            </div>
                        </header>
                        <div className="grid md:grid-cols-2 gap-4">
                            {g.items.map((p) => (
                                <PromiseCard key={p.id} promise={p} />
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
